"use client";

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import brandLogo from "@/assets/brand-logo.png";
import brandMark from "@/assets/brand-mark.png";

type BrandLogoProps = {
  /** Full wordmark image, mark + text lockup, or mark alone */
  variant?: "full" | "lockup" | "mark";
  /** Use light text for dark surfaces (footer, sidebar) */
  onDark?: boolean;
  className?: string;
  imgClassName?: string;
  asLink?: boolean;
  to?: string;
  alt?: string;
};

/**
 * Official Resilience365 brand mark / wordmark.
 * - full: transparent PNG wordmark (best on light backgrounds)
 * - lockup: mark + Resilience365 text (works on light and dark via onDark)
 * - mark: icon only
 */
const BrandLogo = ({
  variant = "full",
  onDark = false,
  className,
  imgClassName,
  asLink = false,
  to = "/",
  alt = "Resilience365",
}: BrandLogoProps) => {
  let content: ReactNode;

  if (variant === "mark") {
    content = (
      <span className={cn("inline-flex items-center", className)}>
        <img
          src={brandMark}
          alt={alt}
          className={cn("h-8 w-8 object-contain", imgClassName)}
          draggable={false}
        />
      </span>
    );
  } else if (variant === "lockup" || onDark) {
    content = (
      <span className={cn("inline-flex items-center gap-2", className)}>
        <img
          src={brandMark}
          alt=""
          aria-hidden
          className={cn("h-8 w-8 object-contain shrink-0", imgClassName)}
          draggable={false}
        />
        <span
          className={cn(
            "font-display font-bold tracking-tight leading-none",
            onDark ? "text-navy-foreground" : "text-foreground",
          )}
        >
          Resilience
          <span className="text-accent">365</span>
        </span>
      </span>
    );
  } else {
    content = (
      <span className={cn("inline-flex items-center", className)}>
        <img
          src={brandLogo}
          alt={alt}
          className={cn("h-8 w-auto object-contain", imgClassName)}
          draggable={false}
        />
      </span>
    );
  }

  if (asLink) {
    return (
      <Link
        to={to}
        className="inline-flex items-center min-w-0 max-w-full"
        aria-label={alt}
      >
        {content}
      </Link>
    );
  }

  return content;
};

export default BrandLogo;
